//path: src\common\ProtectedRoute.tsx

import { useAuthUser } from '../hooks/useAuthUser';
import React, { useEffect, useState } from 'react';

import { Navigate, useLocation } from 'react-router-dom';
import { useArrivalReturnPath } from '@/hooks/useArrivalReturnPath';
import { rememberReturnPath } from '@/modules/auth/returnAfterLogin';
import { LoadingSpinner } from './LoadingSpinner';
import LureoButton from './LureoButton';
import { TextLink } from '@/components/ui/text-button';

interface ProtectedRouteProps {
  element: React.ReactElement; // Define the type for `element`
  /** Signed out, the visitor comes back to this page after login. */
  returnAfterLogin?: boolean;
}

export const RedirectToLogin = ({ search, returnTo }: { search: string; returnTo: string | null }) => {
  useEffect(() => {
    if (returnTo) rememberReturnPath(returnTo);
  }, [returnTo]);
  return <Navigate to={{ pathname: '/login', search }} replace />;
};

const SHOW_AFTER_MS = 300;
const SLOW_AFTER_MS = 20_000;

export const AuthPending = () => {
  const [stage, setStage] = useState<'quiet' | 'waiting' | 'slow'>('quiet');
  useEffect(() => {
    const show = setTimeout(() => setStage('waiting'), SHOW_AFTER_MS);
    const slow = setTimeout(() => setStage('slow'), SLOW_AFTER_MS);
    return () => {
      clearTimeout(show);
      clearTimeout(slow);
    };
  }, []);
  if (stage === 'quiet') return <div />;
  return (
    <div role="status" aria-busy="true" className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
      <LoadingSpinner tone="brand" />
      <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle">Signing you in...</p>
      {stage === 'slow' && (
        <p className="font-lureo-font-prose text-lureo-13 text-lureo-ink-subtle">
          This is taking longer than usual. <TextLink href="/login">Back to sign in</TextLink>
        </p>
      )}
    </div>
  );
};

export const AccountLoadFailed = ({ onRetry }: { onRetry: () => void }) => (
  <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
    <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle">We couldn't load your account.</p>
    <LureoButton variant="secondary" className="h-9 rounded-lureo-radius-button-pill px-4" onClick={onRetry}>
      Try again
    </LureoButton>
  </div>
);

export const ProtectedRoute = ({ element, returnAfterLogin = false }: ProtectedRouteProps) => {
  const { user, loading } = useAuthUser();
  // Preserve the query string across the redirect so UTM params from
  // marketing links survive until PostHog reads them on /login.
  const location = useLocation();
  const returnTo = useArrivalReturnPath(!!user);

  if (loading) return <AuthPending />;
  if (user) return element;
  if (returnAfterLogin) return <RedirectToLogin search="" returnTo={returnTo} />;
  return <RedirectToLogin search={location.search} returnTo={returnTo} />;
};
